//* 부고 등록·수정 폼의 상주 말씀 입력 — 빈칸에 포커스하면 자주 쓰는 문구를 추천한다
//* 추천 = 내 즐겨찾기 먼저 + 프리셋(많이 쓰는 순), 합쳐서 5개까지. 누르면 그 문구로 채워지고 추천은 닫힌다.
//* 값이 있으면(직접 입력 포함) 추천을 띄우지 않는다. 스펙: docs/bugo-mourner-text-favorites-spec.md
import { FC, useMemo, useState } from 'react';

import { useMournerTextPresets } from '@shared/api/fevent/mournerTextPreset';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Control, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';

import { SuggestionDropdown, SuggestionItem } from '../../components/SuggestionDropdown';
import { readMyDetailList } from '../../utils/myDetailList.util';

export const MOURNER_TEXT_SUGGESTION_LIMIT = 5;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  name: string;
  className?: string;
}

export type MournerTextSuggestion = SuggestionItem;

//* 내 즐겨찾기 먼저, 그 다음 프리셋(서버가 count 내림차순으로 준다). 중복 제거 후 limit 개
export const buildMournerTextSuggestions = (
  myList: string[],
  presetTexts: { key: string; text: string }[],
  limit = MOURNER_TEXT_SUGGESTION_LIMIT,
): MournerTextSuggestion[] => {
  const seen = new Set<string>();
  const out: MournerTextSuggestion[] = [];
  for (const text of myList) {
    if (out.length >= limit) break;
    if (!text || seen.has(text)) continue;
    seen.add(text);
    out.push({ key: `my-${text}`, text, mine: true });
  }
  for (const p of presetTexts) {
    if (out.length >= limit) break;
    if (!p.text || seen.has(p.text)) continue;
    seen.add(p.text);
    out.push({ key: `preset-${p.key}`, text: p.text, mine: false });
  }
  return out;
};

const MournerTextPicker: FC<Props> = ({
  control,
  setValue,
  register,
  name,
  className,
}) => {
  const { userProfile, bugoCamelRole } = useAuth();
  const camelRole = bugoCamelRole();
  const myList = useMemo(
    () => readMyDetailList(userProfile, camelRole, 'mournerTextList'),
    [userProfile, camelRole],
  );
  const { data: presets } = useMournerTextPresets();
  const suggestions = useMemo(
    () =>
      buildMournerTextSuggestions(myList, [
        ...(presets?.featured ?? []),
        ...(presets?.more ?? []),
      ]),
    [myList, presets],
  );

  const currentText: string = useWatch({ control, name }) ?? '';
  const [focused, setFocused] = useState(false);
  const open = focused && currentText.trim() === '' && suggestions.length > 0;

  const { onBlur: registerOnBlur, ...field } = register(name);

  const onPick = (text: string) => {
    setValue(name, text, { shouldDirty: true });
  };

  return (
    <div className="relative">
      <textarea
        placeholder="상주 말씀 (비어 있을 때 누르면 자주 쓰는 문구를 추천합니다)"
        className={className}
        onFocus={() => setFocused(true)}
        {...field}
        onBlur={(e) => {
          setFocused(false);
          registerOnBlur(e);
        }}
      />
      {open && (
        <SuggestionDropdown
          title="자주 쓰는 문구"
          ariaLabel="자주 쓰는 상주말씀 추천"
          items={suggestions}
          onPick={onPick}
          manageTo="/user/prefer/mourner-text"
          manageLabel="내 문구 관리"
        />
      )}
    </div>
  );
};

export default MournerTextPicker;
